/**
 * Utility class for managing IntersectionObservers.
 */
class IntersectionObserverUtility {
  /**
   * Creates an instance of IntersectionObserverUtility.
   * @param {Object} options - The options object for the IntersectionObserver.
   * @param {Element} [options.root=null] - The element that is used as the viewport for checking visibility of the target.
   * @param {string} [options.rootMargin='0px'] - Margin around the root. Can have values similar to the CSS margin property.
   * @param {number|number[]} [options.threshold=0.1] - A single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed.
   */
  constructor(options = { root: null, rootMargin: "0px", threshold: 0.1 }) {
    this.options = options;
    this.observer = new IntersectionObserver(
      this.handleIntersect.bind(this),
      this.options,
    );
    this.targets = new Map();
  }

  /**
   * Observes the elements matching the provided selector and triggers the callbacks based on their intersection status.
   * @param {string} selector - The CSS selector for the target elements to observe.
   * @param {Function} onVisibleCallback - The callback function to execute when the target elements intersect with the viewport.
   * @param {Function} [onNotVisibleCallback] - The optional callback function to execute when the target elements are no longer visible.
   * @throws {Error} If the selector is not a valid CSS selector.
   * @throws {Error} If any of the callback functions are not provided.
   */
  observe(selector, onVisibleCallback, onNotVisibleCallback) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
      this.targets.set(element, {
        onVisibleCallback,
        onNotVisibleCallback,
        hasBeenVisible: false,
      });
      this.observer.observe(element);
    });
  }

  /**
   * Stops observing the specified element.
   * @param {Element} element - The target element to stop observing.
   * @throws {Error} If element is not provided.
   * @throws {Error} If observer fails to unobserve the element.
   */
  unobserve(element) {
    this.targets.delete(element);
    this.observer.unobserve(element);
  }

  /**
   * Handles intersection events.
   * @param {IntersectionObserverEntry[]} entries - The intersection observer entries.
   * @throws {Error} If entries is not provided or is not an array.
   * @throws {Error} If any of the required properties (get, onVisibleCallback, onNotVisibleCallback) are missing in targetData.
   */
  handleIntersect(entries) {
    entries.forEach((entry) => {
      const targetData = this.targets.get(entry.target);
      if (entry.isIntersecting) {
        if (targetData && targetData.onVisibleCallback) {
          targetData.onVisibleCallback(entry.target);
          targetData.hasBeenVisible = true;
        }
      } else {
        if (
          targetData &&
          targetData.hasBeenVisible &&
          targetData.onNotVisibleCallback
        ) {
          targetData.onNotVisibleCallback(entry.target);
        }
      }
    });
  }
}

// Create a singleton instance of IntersectionObserverUtility
const $observer = new IntersectionObserverUtility();

export { $observer, IntersectionObserverUtility };
